import { useContext, useEffect, useReducer } from "react";
import { Button, Col, Form, InputGroup, Row, Table } from "react-bootstrap";
import { EditOutlined, DeleteOutlineOutlined, DoneOutlineOutlined }   from '@mui/icons-material';
import { IconButton, Tooltip } from "@mui/material";
import {
  BMI,
  GESTATIONAL_WEIGHT_GAIN_TARGET,
  ADD,
  WEIGHT,
  NEW_WEIGHT,
  PRE_PREGNANCY_WEIGHT2,
  PATIENT_WEIGHT_CREATE,
  PATIENT_WEIGHT_UPDATE,
  PATIENT_WEIGHT_DELETE,
} from "../../util/const";
import { CalculateWeightMetrics } from "../../util/utils";
import { FormatDateForDisplay, GetCurrentDate, GetCurrentTime } from "../../util/data-utils";
import { ReviewContext } from "../ReviewPatient";
import { DatabaseManager } from "../../services/DatabaseManager";
import { Weight } from "../../types/observation";

const observationKey = "weight";
const today = GetCurrentDate();

const initialState = {
  weights: [] as Weight[],
  bmi: 0,
  gwg: "",
  currentWeight: {
    when: today,
    value: ""
  },
  editingRow: -1,
  hasChanged: false
};

const enum REDUCER_ACTION_TYPE {
  WEIGHT_ADDED,
  WEIGHT_UPDATED,
  WEIGHT_DELETED,
  CURRENT_WEIGHT_CHANGE,
  EDITING_ROW_CHANGE,
  EDITING_ROW_DATA_CHANGE,
  DATA_LOADED,
}
type DataLoadedAction = {
  type: REDUCER_ACTION_TYPE.DATA_LOADED,
  payload: {
    bmi: number,
    gwg: string,
    weights: Weight[]
  }
}
type WeightAddedAction = {
  type: REDUCER_ACTION_TYPE.WEIGHT_ADDED,
  payload: Weight
}
type WeightUpdatedAction = {
  type: REDUCER_ACTION_TYPE.WEIGHT_UPDATED,
  payload: {
    weight: Weight,
    index: number
  }
}
type WeightDeletedAction = {
  type: REDUCER_ACTION_TYPE.WEIGHT_DELETED,
  payload: string
}
type WeightChangeAction = {
  type: REDUCER_ACTION_TYPE.CURRENT_WEIGHT_CHANGE,
  payload: {
    when: string,
    value: string
  }
}
type EditRowAction = {
  type: REDUCER_ACTION_TYPE.EDITING_ROW_CHANGE,
  payload: number
}
type EditRowChangeAction = {
  type: REDUCER_ACTION_TYPE.EDITING_ROW_DATA_CHANGE,
  payload: {
    rowDate: string,
    rowWeight: string
  }
}

const WeightTable = () => {

  const context = useContext(ReviewContext);
  const patient = context.patient;
  
  const reducer = (state: typeof initialState, action: DataLoadedAction | WeightAddedAction | WeightUpdatedAction | WeightDeletedAction 
                                                      | EditRowAction | WeightChangeAction | EditRowChangeAction): typeof 
  initialState => {
    switch(action.type)
    {
      case REDUCER_ACTION_TYPE.DATA_LOADED: {
        return {...state, bmi: action.payload.bmi, gwg: action.payload.gwg, weights: action.payload.weights}
      }
      case REDUCER_ACTION_TYPE.WEIGHT_ADDED: {
        var existing = state.weights;
        existing.push(action.payload);
        return {...state, weights: existing, currentWeight: {when: today, value: ""}}
      }
      case REDUCER_ACTION_TYPE.WEIGHT_UPDATED: {
        var updateable = state.weights;
        updateable[action.payload.index] = action.payload.weight;
        return {...state, weights: updateable, editingRow: -1}
      }
      case REDUCER_ACTION_TYPE.WEIGHT_DELETED: {
        var filtered =  state.weights.filter(function(aWeight) { 
          return aWeight.resourceId !== action.payload 
        });
        return {...state, weights: filtered}
      }
      case REDUCER_ACTION_TYPE.CURRENT_WEIGHT_CHANGE: {
        return {...state, currentWeight: { when: action.payload.when, value: action.payload.value}}
      }
      case REDUCER_ACTION_TYPE.EDITING_ROW_CHANGE: {
        return {...state, editingRow: action.payload}
      }
      case REDUCER_ACTION_TYPE.EDITING_ROW_DATA_CHANGE: {
        var hasChanged = false;
        var index = state.editingRow;
        updateable = state.weights;
        if(updateable[index].recordedDate !== action.payload.rowDate)
        {
          updateable[index].recordedDate = action.payload.rowDate;
          hasChanged = true;
        }
          
        if(updateable[index].reading !== action.payload.rowWeight)
        {
          updateable[index].reading = action.payload.rowWeight;
          hasChanged = true;
        }
        if(hasChanged)
          return {...state, weights: updateable, hasChanged: true }
        else 
          return {...state, hasChanged: false }
      }
      default: return state;
    }
  }
  const [weightState, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const [bmi, gwg] = CalculateWeightMetrics(patient.height, patient.weight);
    const payload = { weights: [] as Weight[], bmi: bmi, gwg: gwg }
    if (patient.observations !== undefined && patient.observations.has(observationKey))
    {
      for (const element of patient.observations.get(observationKey)!)
      {
        payload.weights.push(JSON.parse(JSON.stringify(element)));
      }
        
    }
    dispatch({type: REDUCER_ACTION_TYPE.DATA_LOADED, payload: payload}) 
  },[patient]);

  const addWeight = () => {

    if(weightState.currentWeight.when.length === 0 || weightState.currentWeight.value.length === 0)
      return;

    let weight = {
      recordedDate: weightState.currentWeight.when,
      recordedTime: GetCurrentTime(),
      reading: weightState.currentWeight.value,
      enterBy: "Clinician"
    } as Weight;

    DatabaseManager.getInstance()
      .post(PATIENT_WEIGHT_CREATE + patient.resourceId, weight)
      .then((response) => {
        context.reload();
        dispatch({type: REDUCER_ACTION_TYPE.WEIGHT_ADDED, payload: weight}); 
      })
      .catch((error) => {
        console.error(error);
      })
  };

  const editWeight = (w: Weight, index: number) => {
    
    if(w === undefined)
    {
      dispatch({type: REDUCER_ACTION_TYPE.EDITING_ROW_CHANGE, payload: -1});
      return;
    }

    if(!weightState.hasChanged)
    {
      dispatch({type: REDUCER_ACTION_TYPE.EDITING_ROW_CHANGE, payload: -1});
      return;
    }
    w.recordedTime = GetCurrentTime();

    DatabaseManager.getInstance()
      .put(PATIENT_WEIGHT_UPDATE, w)
      .then(() => {
        context.reload();
        dispatch({type: REDUCER_ACTION_TYPE.WEIGHT_UPDATED, payload: {weight: w, index: index}}) 
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const deleteWeight = (resourceId: string) => {
    DatabaseManager.getInstance()
      .delete(PATIENT_WEIGHT_DELETE + resourceId)
      .then((response) => {
        context.reload();
        dispatch({type: REDUCER_ACTION_TYPE.WEIGHT_DELETED, payload: resourceId})
      })
      .catch((error) => {
        console.error(error);
      })
  };

  return (
    <Row className="mt-3">
      <Col sm={6}>
        <Table bordered hover striped className="review-table">
          <thead>
          <tr>
            <th colSpan={4}>  
              <Row>
                {/* <Col className="left">
                  <DataNavigator typeKey="weight" />
                </Col> */}
                <Col className="right me-2 light">
                  {PRE_PREGNANCY_WEIGHT2}: {patient.weight} | {BMI}: {weightState.bmi} | {GESTATIONAL_WEIGHT_GAIN_TARGET}: {weightState.gwg}
                </Col>
              </Row>
            </th>
          </tr>
          <tr>
            <th colSpan={4} className="section"></th>
          </tr>
          <tr>
            <th className="small-col">Date</th>
            <th className="med-col">{WEIGHT} (kg)</th>
            <th className="med-col">Recorded By</th>
            <th className="small-col">Action</th>
          </tr>
          </thead>
          <tbody>
          { weightState.weights.map((element: Weight, i: number) => {
            if(weightState.editingRow === i)
            {
              return(<tr id={i.toString()}>
                <td className="small-col centre"><Form.Control
                required 
                type="date"
                value={element.recordedDate}
                onChange={(e) => {
                  dispatch({type: REDUCER_ACTION_TYPE.EDITING_ROW_DATA_CHANGE, payload: {rowDate: e.target.value, rowWeight: element.reading}})
                }}>
                </Form.Control></td>
                <td><InputGroup>
                  <Form.Control type="text" 
                    value={element.reading} 
                    onChange={(e) => { 
                      dispatch({type: REDUCER_ACTION_TYPE.EDITING_ROW_DATA_CHANGE, payload: {rowDate: element.recordedDate, rowWeight: e.target.value}})
                    }}/>
                  <InputGroup.Text id="basic-addon2">kg</InputGroup.Text>
                </InputGroup></td>
                <td>{element.enterBy}</td>
                <td>{<Tooltip title="Done">
                  <IconButton onClick={(e) => editWeight(element, i)} color="success">
                    <DoneOutlineOutlined />
                  </IconButton></Tooltip>}
                </td>
              </tr>);
            }
            return(<tr id={element.resourceId}>
              <td className="small-col centre">{FormatDateForDisplay(element.recordedDate)}</td>
              <td>{element.reading}</td>
              <td>{element.enterBy}</td>
              <td className="centre">{element.enterBy === "Clinician" ? 
                <Row>
                  <Col></Col>
                  <Col>
                    <Tooltip title="Edit">
                      <IconButton onClick={(e) => dispatch({type: REDUCER_ACTION_TYPE.EDITING_ROW_CHANGE, payload: i}) }>
                        <EditOutlined sx={{color:"#78909C"}}/>
                      </IconButton>
                    </Tooltip>
                  </Col>
                  <Col>
                    <Tooltip title="Delete">
                      <IconButton onClick={(e) => deleteWeight(element.resourceId)} color="warning">
                        <DeleteOutlineOutlined />
                      </IconButton>
                    </Tooltip>
                  </Col>
                  <Col></Col>
                </Row>
                 : <></>}
              </td>
            </tr>);      
            })
          }
          </tbody>
        </Table>
      </Col>
      <Col>
        <Col className="mb-2 fs-6 title">
          {NEW_WEIGHT}
        </Col>
        <Form noValidate>
          <Row>
            <Col sm={2}>
              <Form.Control
                required 
                type="date"
                value={weightState.currentWeight.when}
                onChange={(e) => {
                  dispatch({type: REDUCER_ACTION_TYPE.CURRENT_WEIGHT_CHANGE, payload: {when: e.target.value, value: weightState.currentWeight.value}})
                }}>
              </Form.Control>
            </Col>
            <Col sm={2}>
              <InputGroup>
                <Form.Control type="text" 
                  value={weightState.currentWeight.value} 
                  onChange={(e) => dispatch({type: REDUCER_ACTION_TYPE.CURRENT_WEIGHT_CHANGE, payload: {when: weightState.currentWeight.when, value: e.target.value}}) }/>
                <InputGroup.Text id="basic-addon2">kg</InputGroup.Text>
              </InputGroup>
            </Col>
            <Col sm={2}>
              <Button id="btn-add" type="button" className='fs-6 btn-color' onClick={addWeight} >{ADD}</Button>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );

};

export default WeightTable;